'use client'
import { Check, Settings2, Globe, Smartphone, Database, Cpu, Zap } from 'lucide-react'
import { motion } from 'framer-motion'

const fadeUp: any = (delay = 0) => ({
  initial: { opacity: 0, y: 50 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }
})

export default function Services() {
  return (
    <section id="services" className="py-5 lg:py-24 relative overflow-hidden">
      <div className="px-5 mb-10 relative z-10 m-auto max-w-5xl">
        <motion.h2 {...fadeUp(0)} className="text-3xl lg:text-5xl font-brand mb-">
          Our Services
        </motion.h2>
        <motion.p {...fadeUp(0.15)} className="text-lg tracking-[-1px] text-muted-foreground max-w-150 font-light">
          Comprehensive development solutions tailored to your business needs and technical requirements.
        </motion.p>
      </div>

      <div className="w-full border-y h-8 bg-muted/10 hidden md:block" />

      <div className="border-dashed md:border-0 border-y m-auto max-w-5xl">
        <div className="border-dashed">

          <div className="grid grid-cols-1 md:grid-cols-3 border-dashed border-b-0 relative border border-t-0">
            <motion.div {...fadeUp(0)} className="p-7 md:p-18 md:col-span-2 border-dashed">
              <span className="text-xs text-yellow-300 tracking-[-0.5px] flex items-center gap-1">
                <Settings2 className="w-3 h-3 inline" />
                Custom Software
              </span>
              <div className="text-3xl tracking-[-1.5px]">Custom Software Development</div>
              <div>
                <p className="text-foreground/70 tracking-[-1px] leading-relaxed">
                  Enterprise-grade applications built with modern architecture and best practices to scale with your business needs.
                </p>
              </div>
              <ul className="tracking-[0.5px] mt-4 text-xs text-foreground/60 font-light space-y-1">
                {['Product engineering from idea to launch.', 'Seamless system & cloud integration.', 'Agile, adaptive development.'].map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                    className="flex items-center gap-1"
                  >
                    <Check size={14} className="text-yellow-400" />
                    {item}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
            <div className="relative w-full h-full max-h-120 hidden md:block">
              <video src="/videos/1.mp4" autoPlay loop muted playsInline className="object-cover h-full w-full absolute opacity-60" style={{ display: 'block', mixBlendMode: 'screen' }} />
              <div className="absolute h-full w-full bg-linear-to-r from-background" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 border-dashed border-b-0 relative border border-t">
            <div className="relative overflow-hidden h-full w-full border-dashed border-r hidden md:block">
              <img src="/images/earth.jpg" className="scale-125 -translate-y-20 absolute h-full w-full" style={{ display: 'block', filter: 'brightness(0.6) saturate(2) contrast(1.2)', mixBlendMode: 'screen' }} />
              <img src="/images/many-hands.webp" className="absolute object-cover w-full object-center translate-y-10 h-full max-h-80 opacity-100" style={{ display: 'block', filter: 'brightness(0.6) saturate(0) contrast(1.4)' }} />
            </div>
            <motion.div {...fadeUp(0)} className="mt-auto p-7 md:p-18 md:col-span-2">
              <span className="text-xs text-sky-400 tracking-[-0.5px] flex items-center gap-1">
                <Globe className="w-3 h-3 inline" />
                Web Apps
              </span>
              <div className="text-3xl tracking-[-1.5px]">Web Application Development</div>
              <div>
                <p className="text-foreground/70 tracking-[-1px] leading-relaxed">
                  High-performance web solutions using cutting-edge technologies for optimal user experience and conversion rates.
                </p>
              </div>
              <ul className="tracking-[0.5px] mt-4 text-foreground/60 text-xs font-light space-y-1">
                {['Responsive, interactive web interfaces.', 'Pixel-perfect UI/UX and animations.', 'Conversion-optimized architecture.'].map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                    className="flex items-center gap-1"
                  >
                    <Check size={14} className="text-sky-500" />
                    {item}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 border-dashed border-b-0 relative border border-t">
            <motion.div {...fadeUp(0)} className="p-7 md:p-18 md:col-span-2 border-dashed border-r">
              <span className="text-xs text-indigo-400 tracking-[-0.5px] flex items-center gap-1">
                <Database className="w-3 h-3 inline" />
                Backend Systems
              </span>
              <div className="text-3xl tracking-[-1.5px]">Backend Systems</div>
              <div>
                <p className="text-foreground/70 tracking-[-1px] leading-relaxed">
                  Robust server architecture, API development, and database design for reliable and scalable applications.
                </p>
              </div>
              <ul className="tracking-[0.5px] mt-4 text-xs text-foreground/60 font-light space-y-1">
                {['RESTful APIs & GraphQL backends.', 'Scalable cloud & database solutions.', 'Secure authentication & data workflows.'].map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                    className="flex items-center gap-1"
                  >
                    <Check size={14} className="text-indigo-500" />
                    {item}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
            <div className="relative w-full h-full max-h-120 hidden md:block">
              <video src="/videos/5.mp4" autoPlay loop muted playsInline className="object-cover h-full w-full absolute opacity-60" style={{ display: 'block', filter: 'brightness(0.6) saturate(0) contrast(1.4)', mixBlendMode: 'screen' }} />
              <div className="absolute h-full w-full bg-linear-to-r from-background" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 border-dashed border-b-0 relative border border-t">
            <div className="relative w-full h-full max-h-120 hidden md:block">
              <video src="/videos/4.mp4" autoPlay loop muted playsInline className="object-cover scale-80 h-full w-full absolute opacity-60" style={{ mixBlendMode: 'screen', filter: 'invert(1) hue-rotate(80deg) brightness(0.7) saturate(4)' }} />
              <img src="/images/two-hands.webp" className="absolute m-auto w-full object-cover h-full" style={{ display: 'block', filter: 'brightness(0.6) saturate(0) contrast(1.4)' }} />
              <div className="absolute h-full w-full bg-linear-to-r from-background" />
            </div>
            <motion.div {...fadeUp(0)} className="p-7 md:p-18 md:col-span-2 border-dashed md:border-l">
              <span className="text-xs text-green-400 tracking-[-0.5px] flex items-center gap-1">
                <Smartphone className="w-3 h-3 inline" />
                Mobile Apps
              </span>
              <div className="text-3xl tracking-[-1.5px]">Mobile Solutions</div>
              <div>
                <p className="text-foreground/70 tracking-[-1px] leading-relaxed">
                  Cross-platform mobile applications that deliver seamless experiences across all devices and platforms.
                </p>
              </div>
              <ul className="tracking-[0.5px] mt-4 text-xs text-foreground/60 font-light space-y-1">
                {['iOS, Android & cross-platform apps.', 'Native performance, modern interfaces.', 'Device integration & push notifications.'].map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                    className="flex items-center gap-1"
                  >
                    <Check size={14} className="text-green-400" />
                    {item}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 border-dashed border-b-0 relative border border-t">
            <motion.div {...fadeUp(0)} className="p-7 md:p-18 md:col-span-2 border-dashed">
              <span className="text-xs text-orange-400 tracking-[-0.5px] flex items-center gap-1">
                <Cpu className="w-3 h-3 inline" />
                DevOps
              </span>
              <div className="text-3xl tracking-[-1.5px]">DevOps & Deployment</div>
              <div>
                <p className="text-foreground/70 tracking-[-1px] leading-relaxed">
                  Complete CI/CD pipeline setup, cloud infrastructure, and deployment strategies for optimal performance.
                </p>
              </div>
              <ul className="tracking-[0.5px] mt-4 text-xs text-foreground/60 font-light space-y-1">
                {['Automated CI/CD, testing & releases.', 'Cloud hosting & container orchestration.', 'Monitoring, scaling, and security.'].map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                    className="flex items-center gap-1"
                  >
                    <Check size={14} className="text-orange-400" />
                    {item}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
            <div className="relative w-full h-full max-h-120 hidden md:block">
              <video src="/videos/2.mp4" autoPlay loop muted playsInline className="object-cover h-full w-full absolute opacity-60" style={{ display: 'block', filter: 'brightness(0.6) saturate(0) contrast(1.4)', mixBlendMode: 'screen' }} />
              <div className="absolute h-full w-full bg-linear-to-r from-background" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 border-dashed border-b-0 relative border border-t">
            <div className="relative w-full h-full max-h-120 hidden md:block">
              <video src="/videos/6.mp4" autoPlay loop muted playsInline className="object-cover h-full w-full absolute opacity-60" style={{ mixBlendMode: 'screen', filter: 'hue-rotate(190deg) saturate(1.4) brightness(1.1)' }} />
              <div className="absolute h-full w-full bg-linear-to-l from-background" />
            </div>
            <motion.div {...fadeUp(0)} className="p-7 md:p-18 md:col-span-2 border-dashed md:border-l">
              <span className="text-xs text-pink-400 tracking-[-0.5px] flex items-center gap-1">
                <Zap className="w-3 h-3 inline" />
                Performance
              </span>
              <div className="text-3xl tracking-[-1.5px]">Performance Optimization</div>
              <div>
                <p className="text-foreground/70 tracking-[-1px] leading-relaxed">
                  Application optimization, speed enhancements, and technical improvements for better user engagement.
                </p>
              </div>
              <ul className="tracking-[0.5px] mt-4 text-xs text-foreground/60 font-light space-y-1">
                {['Frontend & backend performance tuning.', 'Code & bundle optimization.', 'Caching, CDN, and server optimization.'].map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                    className="flex items-center gap-1"
                  >
                    <Check size={14} className="text-pink-400" />
                    {item}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>

        </div>
      </div>

      <div className="w-full border-y h-8 bg-muted/10 hidden md:block" />
    </section>
  )
}